/* eslint-disable security/detect-object-injection */
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  HeaderContext,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  cn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { useMemo, useState } from 'react';

import { Icon } from '@/components/widgets/a/Icon/Icon';
import { isNullish } from '@/utils/booleans';
import { mergeColumns, MetadataColumn } from '@/utils/table/mergeColumns';

type ColumnTooltipProps = {
  tooltipContent: string;
};

const ColumnTooltip = ({ tooltipContent }: ColumnTooltipProps) => (
  <Tooltip>
    <TooltipTrigger className='items-center'>
      <Icon icon='ri-information-line' className='text-active-54 typography-body1' />
    </TooltipTrigger>
    <TooltipContent>{tooltipContent}</TooltipContent>
  </Tooltip>
);

export type GenericTableProps<T> = {
  data: T[];
  defaultColumns: MetadataColumn<T>[];
  customColumns?: MetadataColumn<T>[];
  title?: string;
  className?: string;
  showTooltips?: boolean;
  pageSize?: number;
  showPagination?: boolean;
  tooltipContent?: Record<string, string>;
  onRowClick?: (row: Row<T>) => void;
  styles?: { cell?: string; row?: string; header?: string; head?: string; title?: string };
  columnOrder?: string[];
  dataTestId?: string;
};

export const GenericTable = <T,>({
  data,
  defaultColumns,
  customColumns,
  title,
  className = '',
  showTooltips = false,
  pageSize,
  tooltipContent = {},
  onRowClick,
  styles,
  columnOrder,
  dataTestId,
}: GenericTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo(() => {
    const mergedColumns = mergeColumns({
      defaultColumns,
      customColumns,
    });

    const orderedColumns = columnOrder
      ? (columnOrder
          .map((key) => mergedColumns.find((col) => col.accessorKey === key))
          .filter(Boolean) as MetadataColumn<T>[])
      : mergedColumns;

    return orderedColumns.map((column) => {
      const accessorKey = column.accessorKey as string;
      const hasTooltip = showTooltips && tooltipContent[accessorKey];

      if (hasTooltip && column.header) {
        const originalHeader = column.header;
        return {
          ...column,
          header: (props: HeaderContext<T, unknown>) => (
            <Stack direction='row' centerMain className='gap-2'>
              {typeof originalHeader === 'function' ? originalHeader(props) : originalHeader}
              <ColumnTooltip tooltipContent={tooltipContent[accessorKey] || ''} />
            </Stack>
          ),
        };
      }

      return column;
    });
  }, [defaultColumns, customColumns, showTooltips, tooltipContent, columnOrder]);

  const table = useReactTable({
    data,
    columns: columns as MetadataColumn<T>[],
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: isNullish(pageSize),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
    state: { sorting },
  });

  return (
    <div
      className={cn('rounded-2xl bg-white-100 shadow-sm border border-neutral-black-12 gap-4', className)}
      data-testid={dataTestId}
    >
      {title && <span className={cn('typography-h3 text-secondary', styles?.title)}>{title}</span>}

      <Table>
        <TableHeader className={styles?.header}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    'typography-overline h-12 px-6 py-4 text-left align-middle leading-4 text-text-secondary',
                    styles?.head,
                  )}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn('border-b border-divider hover:bg-neutral-black-4', styles?.row)}
              onClick={() => onRowClick?.(row)}
            >
              {row.getVisibleCells().map((cell) => {
                const cellMeta = (cell.column.columnDef.meta ?? {}) as { styles?: { cell?: string } };

                return (
                  <TableCell
                    key={cell.id}
                    className={cn('typography-body1 p-6 align-middle', styles?.cell, cellMeta.styles?.cell)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
